import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '@/config/authConfig';

interface UserInfo {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isReady: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: (forceRefresh?: boolean) => Promise<string | null>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();
  const [error, setError] = useState<string | null>(null);

  // Convert MSAL account to our UserInfo type
  const user: UserInfo | null = account
    ? {
        id: account.localAccountId || account.homeAccountId,
        email: account.username || '',
        name: account.name || account.username || 'User',
      }
    : null;

  // Handle redirect results
  useEffect(() => {
    const handleRedirectPromise = async () => {
      if (
        !window.location.href.includes('code=') &&
        !window.location.href.includes('error=')
      ) {
        return;
      }

      try {
        await instance.handleRedirectPromise();
      } catch (err) {
        console.error('Authentication redirect failed:', err);
        if (err instanceof Error) {
          if (err.message.includes('interaction_in_progress')) {
            return;
          }
        }
      }
    };

    const timer = setTimeout(handleRedirectPromise, 100);
    return () => clearTimeout(timer);
  }, [instance]);

  const login = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account',
      });
    } catch (err) {
      console.error('Login failed:', err);
      if (err instanceof Error) {
        if (err.message.includes('interaction_in_progress')) {
          setError('Login already in progress. Please wait...');
          return;
        }
        setError(err.message);
      }
      throw err;
    }
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      const allAccounts = instance.getAllAccounts();
      for (const acc of allAccounts) {
        await instance.clearCache({ account: acc });
      }

      await instance.logoutRedirect({
        postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || '/',
      });
    } catch (err) {
      console.error('Logout failed:', err);
      sessionStorage.clear();
      window.location.href = '/';
    }
  }, [instance]);

  // Cache for the current token and its expiration
  const tokenCacheRef = useRef<{ token: string; expiresOn: Date } | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Proactively refresh token before it expires (5 minutes buffer)
  const scheduleTokenRefresh = useCallback((expiresOn: Date) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const now = Date.now();
    const expiresAt = expiresOn.getTime();
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes before expiry
    const refreshIn = expiresAt - now - refreshBuffer;

    if (refreshIn > 0) {
      console.log(`[Auth] Scheduling token refresh in ${Math.round(refreshIn / 1000)}s`);
      refreshTimerRef.current = setTimeout(async () => {
        console.log('[Auth] Proactively refreshing token...');
        try {
          await getAccessTokenInternal(true);
        } catch (err) {
          console.warn('[Auth] Proactive refresh failed:', err);
        }
      }, refreshIn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Internal function to acquire token
  const getAccessTokenInternal = useCallback(async (forceRefresh = false): Promise<string | null> => {
    if (!account) {
      console.warn('[Auth] No account available for token acquisition');
      return null;
    }

    // Clear cache if forcing refresh (e.g., after 401 error)
    if (forceRefresh) {
      console.log('[Auth] Force refresh requested, clearing token cache');
      tokenCacheRef.current = null;
    }

    // Return cached token if still valid (with 1 minute buffer)
    if (!forceRefresh && tokenCacheRef.current) {
      const bufferMs = 60 * 1000; // 1 minute buffer
      if (tokenCacheRef.current.expiresOn.getTime() - Date.now() > bufferMs) {
        return tokenCacheRef.current.token;
      }
      console.log('[Auth] Cached token expired, fetching new one');
    }

    try {
      // Try silent token acquisition (uses refresh token automatically)
      const response: AuthenticationResult = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account as AccountInfo,
        forceRefresh, // Force refresh if explicitly requested
      });

      // Cache the new token
      if (response.expiresOn) {
        tokenCacheRef.current = {
          token: response.idToken,
          expiresOn: response.expiresOn,
        };
        scheduleTokenRefresh(response.expiresOn);
      }

      console.log('[Auth] Token acquired successfully via silent flow');
      return response.idToken;
    } catch (silentError) {
      // Check if interaction is required (refresh token expired or consent needed)
      if (silentError instanceof InteractionRequiredAuthError) {
        console.warn('[Auth] Silent token acquisition failed, interaction required:', silentError.errorCode);
        
        try {
          // Try popup first for better UX
          const response = await instance.acquireTokenPopup({
            ...loginRequest,
            account: account as AccountInfo,
          });

          if (response.expiresOn) {
            tokenCacheRef.current = {
              token: response.idToken,
              expiresOn: response.expiresOn,
            };
            scheduleTokenRefresh(response.expiresOn);
          }

          console.log('[Auth] Token acquired via popup');
          return response.idToken;
        } catch (popupError) {
          console.warn('[Auth] Popup failed, falling back to redirect:', popupError);
          
          // If popup fails (blocked), fall back to redirect
          try {
            await instance.acquireTokenRedirect({
              ...loginRequest,
              account: account as AccountInfo,
            });
            // This won't return - page will redirect
            return null;
          } catch (redirectError) {
            console.error('[Auth] All token acquisition methods failed:', redirectError);
            setError('Session expired. Please log in again.');
            return null;
          }
        }
      }

      // Handle other errors
      console.error('[Auth] Token acquisition failed:', silentError);
      return null;
    }
  }, [instance, account, scheduleTokenRefresh]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Public getAccessToken function - accepts optional forceRefresh parameter
  const getAccessToken = useCallback(async (forceRefresh = false): Promise<string | null> => {
    return getAccessTokenInternal(forceRefresh);
  }, [getAccessTokenInternal]);

  const isLoading =
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Startup;

  const isReady =
    inProgress === InteractionStatus.None &&
    isAuthenticated &&
    accounts.length > 0 &&
    !!account;

  const value: AuthContextType = {
    isAuthenticated,
    isReady,
    isLoading,
    user,
    login,
    logout,
    getAccessToken,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
